import { useCallback, useEffect, useState } from 'react';
import { waitUntil } from '../../../common/utils/functionUtils';
import { manuallyReportError } from '../../analytics/rollbarService';
import { reloadOneTrustBanner, removeCookieBannerStyles } from './oneTrustUtils';
import { isInIFrame } from '../../utils/services/iframe';
import getClientConfig from '../../utils/getClientConfig';
import { ONE_TRUST_STATE } from './oneTrustConstants';

const oneTrustLoaded = () => !!window.OneTrust && !!window.OneTrust.Init && !!window.OneTrust.GetDomainData;

const useOneTrustLoader = ({ isGuest, userId, userOTToken }) => {
    const config = getClientConfig();

    const [oneTrustState, setOneTrustState] = useState(ONE_TRUST_STATE.UNFETCHED);

    const loadOneTrust = useCallback(() => {
        // If OneTrust has not been loaded yet, embed OneTrust script into the DOM
        if (oneTrustState === ONE_TRUST_STATE.UNFETCHED) {
            const { domainScript } = config.oneTrust;

            const script = document.createElement('script');
            script.src = `https://cdn.cookielaw.org/consent/${domainScript}/otSDKStub.js`;
            script.dataset.domainScript = domainScript;

            document.body.appendChild(script);

            setOneTrustState(ONE_TRUST_STATE.FETCHING);

            waitUntil(oneTrustLoaded, { duration: 60000 })
                .then(() => {
                    setOneTrustState(ONE_TRUST_STATE.INITIALISED);
                })
                .catch((error) => {
                    setOneTrustState(ONE_TRUST_STATE.ERROR);
                    manuallyReportError({ errorMessage: 'OneTrust failed to load', error });
                    removeCookieBannerStyles();
                });

            return;
        }

        // If OneTrust has previously been loaded, just reload the cookie banner
        if (oneTrustState === ONE_TRUST_STATE.INITIALISED) {
            reloadOneTrustBanner();
        }
    }, [oneTrustState]);

    useEffect(() => {
        // If in iframe, don't show cookie banner, and use default cookie settings per region
        if (isInIFrame()) return;

        // If user authenticated, set user information and load OneTrust
        // - This will be used to get user's cookie preference from OneTrust cloud, or send user's cookie preference
        //   to OneTrust Cloud
        // - This will help in Safari, where user's local cookie pref is removed by Safari every 7 days due to ITP
        if (!isGuest && userId && userId !== 'guest') {
            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {
                id: userId,
                isAnonymous: false,
                token: userOTToken,
            };

            loadOneTrust();
        }

        if (isGuest) {
            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {};

            loadOneTrust();
        }
    }, [isGuest, userId]);

    return [oneTrustState];
};

export default useOneTrustLoader;
